import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';
import { useData } from './DataContext';

export const PITCH_STATUSES = [
  { id: 'pitched', label: 'pitched', color: '#38bdf8' },
  { id: 'follow_up', label: 'follow up', color: '#f59e0b' },
  { id: 'interested', label: 'interested', color: '#d394ab' },
  { id: 'declined', label: 'declined', color: '#94a3b8' },
  { id: 'converted', label: 'converted', color: '#059669' },
];

const PitchesContext = createContext(null);

export const PitchesProvider = ({ children }) => {
  const { user } = useAuth();
  const { refresh: refreshData } = useData();
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setPitches([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/pitches');
      setPitches(data || []);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // If multiple talent are tagged, create one independent pitch per talent so each of their
  // trackers gets its own copy. Selecting nobody keeps the single shared "full roster" pitch.
  const addPitch = async (payload) => {
    const ids = payload.talent_ids && payload.talent_ids.length ? payload.talent_ids : [];
    const base = {
      title: payload.title,
      brand_id: payload.brand_id || null,
      contact_id: payload.contact_id || null,
      status: payload.status || 'pitched',
      pitched_date: payload.pitched_date || null,
      last_contact_date: payload.last_contact_date || null,
      notes: payload.notes || '',
      note_entries: payload.note_entries || undefined,
    };
    if (ids.length > 1) {
      const created = [];
      for (const tid of ids) {
        const { data } = await api.post('/pitches', { ...base, talent_id: tid, talent_ids: [tid] });
        created.push(data);
      }
      setPitches((prev) => [...created, ...prev]);
      return created[0];
    }
    const clean = { ...base, talent_id: ids[0] || null, talent_ids: ids };
    const { data } = await api.post('/pitches', clean);
    setPitches((prev) => [data, ...prev]);
    return data;
  };

  const updatePitch = async (id, patch) => {
    const clean = { ...patch };
    if ('talent_ids' in clean) {
      clean.talent_ids = clean.talent_ids || [];
      clean.talent_id = clean.talent_ids[0] || null;
    }
    const { data } = await api.patch(`/pitches/${id}`, clean);
    setPitches((prev) => prev.map((p) => (p.id === id ? data : p)));
    return data;
  };

  const removePitch = async (id) => {
    await api.delete(`/pitches/${id}`);
    setPitches((prev) => prev.filter((p) => p.id !== id));
  };

  const convertPitch = async (id, payload) => {
    const { data } = await api.post(`/pitches/${id}/convert`, payload);
    setPitches((prev) => prev.map((p) => (p.id === id ? data.pitch : p)));
    // refresh deals so the new one shows up on /deals
    await refreshData();
    return data;
  };

  return (
    <PitchesContext.Provider value={{ pitches, loading, statuses: PITCH_STATUSES, addPitch, updatePitch, removePitch, convertPitch, refresh }}>
      {children}
    </PitchesContext.Provider>
  );
};

export const usePitches = () => {
  const c = useContext(PitchesContext);
  if (!c) throw new Error('usePitches must be used within PitchesProvider');
  return c;
};
